import { buildMasterIdentityKey } from "@carry/shared";
import type { ResolvedCoords } from "./order-payload";
import type { DraftOrder } from "./types";

type Coord = { lat: number; lng: number };

interface AutoMasterApi {
  deliverySpot: {
    create(input: {
      name: string;
      address: string;
      latitude: string;
      longitude: string;
      phone?: string;
      email?: string;
      person_name?: string;
      person_name_kana?: string;
      branch?: string;
      building?: string;
      handover_location?: string;
    }): Promise<{ data: { id: string } | null }>;
  };
  pickupSpot: {
    create(input: {
      spot_name: string;
      address: string;
      latitude: string;
      longitude: string;
    }): Promise<{ data: { id: string } | null }>;
  };
}

interface EnsureUnselectedOrderMastersInput {
  api: AutoMasterApi;
  draft: DraftOrder;
  coords: ResolvedCoords;
  geocodeAddress?: (address: string) => Promise<Coord | null>;
  cache?: OrderMasterCache;
}

export interface EnsureUnselectedOrderMastersResult {
  draft: DraftOrder;
  coords: ResolvedCoords;
  createdDeliverySpot: boolean;
  createdPickupSpot: boolean;
}

const trimmed = (value: string) => value.trim();
const optionalTrimmed = (value: string) => {
  const next = value.trim();
  return next.length > 0 ? next : undefined;
};
const coordString = (value: number) => String(value);

export interface OrderMasterCache {
  deliverySpotIds: Map<string, string>;
  pickupSpotIds: Map<string, string>;
}

export function createOrderMasterCache(): OrderMasterCache {
  return {
    deliverySpotIds: new Map(),
    pickupSpotIds: new Map(),
  };
}

export async function ensureUnselectedOrderMasters({
  api,
  draft,
  coords,
  geocodeAddress,
  cache,
}: EnsureUnselectedOrderMastersInput): Promise<EnsureUnselectedOrderMastersResult> {
  const nextDraft: DraftOrder = { ...draft };
  const nextCoords: ResolvedCoords = { ...coords };
  let createdDeliverySpot = false;
  let createdPickupSpot = false;

  if (!nextDraft.delivery_spot_public_id) {
    const name = trimmed(nextDraft.delivery_company);
    const address = trimmed(nextDraft.delivery_address);
    const key = buildMasterIdentityKey({ address, name });
    const cachedId = key ? cache?.deliverySpotIds.get(key) : undefined;
    if (cachedId) {
      nextDraft.delivery_spot_public_id = cachedId;
    }
    let deliveryCoord = nextCoords.delivery;
    if (!nextDraft.delivery_spot_public_id && !deliveryCoord && address && geocodeAddress) {
      deliveryCoord = await geocodeAddress(address);
      nextCoords.delivery = deliveryCoord;
    }
    if (name && address && deliveryCoord) {
      if (!nextDraft.delivery_spot_public_id) {
        const res = await api.deliverySpot.create({
          name,
          address,
          latitude: coordString(deliveryCoord.lat),
          longitude: coordString(deliveryCoord.lng),
          phone: optionalTrimmed(nextDraft.delivery_phone),
          email: optionalTrimmed(nextDraft.delivery_email),
          person_name: optionalTrimmed(nextDraft.delivery_person_name),
          person_name_kana: optionalTrimmed(nextDraft.delivery_person_name_kana),
          branch: optionalTrimmed(nextDraft.delivery_branch),
          building: optionalTrimmed(nextDraft.delivery_building),
          handover_location: optionalTrimmed(nextDraft.handover_location),
        });
        if (res.data?.id) {
          nextDraft.delivery_spot_public_id = res.data.id;
          if (key) cache?.deliverySpotIds.set(key, res.data.id);
          createdDeliverySpot = true;
        }
      }
    }
  }

  if (!nextDraft.pickup_spot_public_id) {
    const spotName = trimmed(nextDraft.store_name);
    const address = trimmed(nextDraft.store_address);
    const key = buildMasterIdentityKey({ address, name: spotName });
    const cachedId = key ? cache?.pickupSpotIds.get(key) : undefined;
    if (cachedId) {
      nextDraft.pickup_spot_public_id = cachedId;
    }
    let pickupCoord = nextCoords.pickup;
    if (!pickupCoord && address && geocodeAddress) {
      pickupCoord = await geocodeAddress(address);
      nextCoords.pickup = pickupCoord;
    }
    if (!nextDraft.pickup_spot_public_id && spotName && address && pickupCoord) {
      const res = await api.pickupSpot.create({
        spot_name: spotName,
        address,
        latitude: coordString(pickupCoord.lat),
        longitude: coordString(pickupCoord.lng),
      });
      if (res.data?.id) {
        nextDraft.pickup_spot_public_id = res.data.id;
        if (key) cache?.pickupSpotIds.set(key, res.data.id);
        createdPickupSpot = true;
      }
    }
  }

  return { draft: nextDraft, coords: nextCoords, createdDeliverySpot, createdPickupSpot };
}
